import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Headphones, Calendar } from "lucide-react";
import { useModals } from "@/context/ModalContext";
import { GOOGLE_MEET_LINK, PHONE, PHONE_HREF } from "@/lib/config";

const GREETING = "Hi! 👋 I'm your Recovery Concierge. How can I help you find your perfect sauna, cold plunge or hot tub?";

const FLOWS = {
  saunas: {
    label: "Shop Saunas",
    reply: "We carry infrared, traditional, barrel & cabin saunas from Harvia, HealthSmart, Finnmark & more. Want to browse, or have me match you?",
    actions: [{ t: "Browse Saunas", to: "/shop/c/saunas" }, { t: "Find My Match", to: "/quiz" }],
  },
  "cold-plunges": {
    label: "Cold Plunges",
    reply: "Cold plunges are perfect for contrast therapy after a sauna. Explore our chiller and cedar options.",
    actions: [{ t: "Browse Cold Plunges", to: "/shop/c/cold-plunges" }],
  },
  heaters: {
    label: "Heaters",
    reply: "We stock electric heaters, wood-burning stoves, stove kits and controls — matched to your sauna.",
    actions: [{ t: "Browse Heaters", to: "/shop/c/sauna-heaters" }],
  },
  shipping: {
    label: "Shipping & White Glove",
    reply: "Accessories ship in 3–5 days; large units ship freight in 2–4 weeks and qualify for white glove delivery & installation.",
    actions: [{ t: "Read Shipping FAQ", to: "/faq" }],
  },
  expert: {
    label: "Talk to an Expert",
    reply: "Our specialists offer free 1-on-1 guidance. Book a video call or request a callback.",
    actions: [{ t: "Book a Call", href: GOOGLE_MEET_LINK }, { t: "Request a Consultation", consult: true }],
  },
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ from: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const { openConsultation } = useModals();
  const navigate = useNavigate();
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const push = (m) => setMessages((s) => [...s, m]);

  const handleFlow = (key) => {
    const f = FLOWS[key];
    push({ from: "user", text: f.label });
    setTimeout(() => push({ from: "bot", text: f.reply, actions: f.actions }), 350);
  };

  const handleAction = (a) => {
    if (a.href) { window.open(a.href, "_blank"); return; }
    if (a.consult) { openConsultation("From chat"); return; }
    if (a.to) { navigate(a.to); setOpen(false); }
  };

  const send = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    push({ from: "user", text: q });
    setInput("");
    const ql = q.toLowerCase();
    let key = null;
    if (/(sauna|infrared|barrel|traditional)/.test(ql)) key = "saunas";
    else if (/(cold|plunge|ice)/.test(ql)) key = "cold-plunges";
    else if (/(heater|stove|control)/.test(ql)) key = "heaters";
    else if (/(ship|deliver|install|white glove|freight)/.test(ql)) key = "shipping";
    else if (/(expert|call|talk|help|consult|book|appointment)/.test(ql)) key = "expert";
    setTimeout(() => {
      if (key) {
        const f = FLOWS[key];
        push({ from: "bot", text: f.reply, actions: f.actions });
      } else {
        push({
          from: "bot",
          text: `Great question! For specifics, our experts can help directly. Call ${PHONE} or pick an option below.`,
          actions: [{ t: "Talk to an Expert", consult: true }, { t: "Find My Match", to: "/quiz" }],
        });
      }
    }, 400);
  };

  return (
    <>
      {!open && (
        <button data-testid="chat-toggle" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-charcoal border border-gold/40 text-gold shadow-2xl shadow-black/50 hover:scale-105 transition-transform">
          <MessageCircle size={24} />
        </button>
      )}

      {open && (
        <div data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[370px] h-[520px] max-h-[80vh] bg-espresso border border-white/15 rounded-xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-charcoal border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <Headphones size={16} className="text-gold" />
              </div>
              <div>
                <p className="text-cream text-sm font-medium leading-none">Recovery Concierge</p>
                <p className="text-forest text-[11px] mt-0.5">● Online now</p>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-cream/60 hover:text-cream">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={m.from === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className="max-w-[80%]">
                  <div className={`px-3 py-2 text-sm rounded-2xl ${m.from === "user"
                    ? "bg-gold text-espresso rounded-br-sm"
                    : "bg-charcoal text-cream/90 rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                  {m.actions && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.actions.map((a) => (
                        <button key={a.t} data-testid={`chat-action-${a.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => handleAction(a)}
                          className="text-xs border border-gold/50 text-gold px-2.5 py-1.5 rounded-full hover:bg-gold/10 transition-colors flex items-center gap-1">
                          {a.href && <Calendar size={12} />}{a.t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {Object.entries(FLOWS).map(([k, f]) => (
                  <button key={k} data-testid={`chat-quick-${k}`} onClick={() => handleFlow(k)}
                    className="text-xs border border-white/20 text-cream/80 px-2.5 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask us anything…"
              className="flex-1 bg-charcoal border border-white/15 rounded-full px-4 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-cream/40" />
            <button data-testid="chat-send" type="submit"
              className="w-9 h-9 rounded-full bg-gold text-espresso flex items-center justify-center hover:bg-amber transition-colors shrink-0">
              <Send size={16} />
            </button>
          </form>
          <a href={PHONE_HREF} className="text-center text-[11px] text-cream/50 hover:text-gold py-1.5 border-t border-white/5">
            Prefer to call? {PHONE}
          </a>
        </div>
      )}
    </>
  );
};
